import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { caseStudies } from "@/mocks/caseStudies";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const caseStudiesJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/case-studies#webpage`,
      "url": `${SITE_URL}/case-studies`,
      "name": "Web Design Case Studies & Portfolio | GK Vertex Consulting",
      "description": "See GK Vertex Consulting's concept work and website transformations. Real examples of premium web design, lead generation, and digital marketing results.",
      "inLanguage": "en-US",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "dateModified": "2026-04-05",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": `${SITE_URL}/case-studies` }
        ]
      }
    },
    {
      "@type": "CollectionPage",
      "url": `${SITE_URL}/case-studies`,
      "name": "Web Design Case Studies & Portfolio",
      "description": "Concept work and website transformations demonstrating the quality and results delivered by GK Vertex Consulting.",
      "inLanguage": "en-US",
      "publisher": {
        "@type": "Organization",
        "name": "GK Vertex Consulting",
        "url": SITE_URL,
        "email": "gkvertexconsulting@gmail.com",
        "logo": {
          "@type": "ImageObject",
          "url": `${SITE_URL}/favicon.ico`
        }
      },
      "about": {
        "@type": "Service",
        "name": "Custom Web Design & Digital Marketing",
        "provider": {
          "@type": "Organization",
          "name": "GK Vertex Consulting"
        }
      }
    },
    {
      "@type": "ItemList",
      "name": "Web Design Case Studies",
      "description": "Portfolio of web design and digital marketing projects by GK Vertex Consulting.",
      "url": `${SITE_URL}/case-studies`,
      "numberOfItems": 3,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "CreativeWork",
            "name": "Law Firm Website Redesign",
            "description": "Complete website transformation for a law firm, resulting in increased lead generation and improved online presence.",
            "creator": { "@type": "Organization", "name": "GK Vertex Consulting" }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "CreativeWork",
            "name": "E-Commerce Brand Website",
            "description": "Custom e-commerce website design with conversion-focused layout and digital marketing integration.",
            "creator": { "@type": "Organization", "name": "GK Vertex Consulting" }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "CreativeWork",
            "name": "Service Business Lead Generation Site",
            "description": "High-converting website for a service business with SEO optimization and lead capture strategy.",
            "creator": { "@type": "Organization", "name": "GK Vertex Consulting" }
          }
        }
      ]
    }
  ]
};

export default function CaseStudiesPage() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "case-studies-jsonld";
    script.text = JSON.stringify(caseStudiesJsonLd);
    document.head.appendChild(script);
    document.title = "Web Design Case Studies & Portfolio | GK Vertex Consulting";
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.setAttribute("content", "See GK Vertex Consulting's concept work and website transformations. Real examples of premium web design, lead generation, and digital marketing results.");
    return () => {
      const el = document.getElementById("case-studies-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 bg-[#050810] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[#0a0e1f] to-[#050810]"></div>
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-[#d4af37]/6 blur-[120px] pointer-events-none"></div>
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 mb-8">
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase">Sample Projects</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-4">
              Concept Work &amp;<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Transformations</span>
            </h1>
            <p className="text-white/50 text-lg max-w-xl mx-auto leading-relaxed">
              These are sample projects and concept work that demonstrate the quality and results we deliver. Real client results coming soon.
            </p>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 bg-[#080b18]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-20">
            {caseStudies.map((study, index) => (
              <div key={study.id} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Visual Panel */}
                <div className={`relative ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-[#0c0f1c] to-[#080b18] flex items-center justify-center min-h-[300px] p-12">
                    <div className="text-center">
                      <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/20 mx-auto mb-5">
                        <i className={`${study.afterIcon} text-[#d4af37] text-4xl`}></i>
                      </div>
                      <div className="text-white font-bold text-xl mb-1">{study.title}</div>
                      <div className="text-white/40 text-sm">{study.category}</div>
                    </div>
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#d4af37]/20 border border-[#d4af37]/30">
                      <span className="text-[#d4af37] text-xs font-semibold">{study.tag}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-3">{study.category}</div>
                  <h2 className="text-3xl md:text-4xl font-black text-white mb-4">{study.title}</h2>
                  <p className="text-white/50 text-base leading-relaxed mb-8">{study.description}</p>

                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {study.metrics.map((metric) => (
                      <div key={metric.label} className="p-4 rounded-xl border border-white/5 bg-[#0c0f1c] text-center">
                        <div className="text-[#d4af37] font-black text-xl mb-1">{metric.value}</div>
                        <div className="text-white/40 text-xs">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {study.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1.5 rounded-full border border-white/10 text-white/50 text-xs">{tag}</span>
                    ))}
                  </div>

                  <a href="/#contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] text-[#050810] font-bold text-sm hover:bg-[#e8c84a] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    Get a Similar Result <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-[#050810] text-center">
          <div className="max-w-2xl mx-auto px-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 mb-8">
              <i className="ri-vip-crown-line text-[#d4af37] text-sm"></i>
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase">Be Our Next Success Story</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to Transform<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Your Website?</span>
            </h2>
            <p className="text-white/50 text-base mb-10 leading-relaxed">
              Join our founding clients and get a premium website build at a discounted rate. Limited spots available.
            </p>
            <a href="/#contact" className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#d4af37] text-[#050810] font-bold text-sm hover:bg-[#e8c84a] transition-all duration-300 whitespace-nowrap cursor-pointer">
              Book a Free Call
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#050810]">
                <i className="ri-arrow-right-line text-[#d4af37] text-sm"></i>
              </span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
